/**
 * v28 BOLT-151 — practice explains itself, from ONE source.
 *
 * Practice is the first thing a new player touches, and it has exactly one
 * mechanic that is not obvious: you move by TAPPING THE MAP, not by walking.
 * v19 taught that with a 9 pt line under the PRACTICE badge, and Jake's field
 * report is that it does not land:
 *
 *   "We have a tiny 'tap the map to walk.' message shown in practice mode, but
 *    it's not obvious and most users misunderstand that they should click on
 *    the map to simulate moving."
 *
 * A hint competes with the map; a modal does not. So the same words are said
 * once, captively, at the top of the round - and the hint stays afterwards as
 * the reminder, which is a different job from the teaching.
 *
 * The copy is Jake's, verbatim but for one thing: he wrote "we've place the
 * hider", and that ships as "we've placed". The copy standard has nothing to
 * say about grammar, so this is a judgement call and it is recorded here as
 * one rather than hidden.
 *
 * ONE SOURCE, BOTH PLATFORMS. The iOS half is `ios/Bolt/Play/PracticeCopy.swift`
 * and `autoBoundaryParity.test.ts` fails the build if a line drifts. That guard
 * exists because this repo has shipped web-only copy four times (the v24
 * landmark harvest, Home Turf, the score label, the card preview).
 */

/**
 * The intro, one paragraph per idea: what this is, how you move, what to do.
 * Split for reading, not for meaning - `PRACTICE_INTRO_BODY` is the sentence
 * Jake actually wrote.
 */
export const PRACTICE_INTRO_LINES = [
  "This is practice mode. You are the seeker, and we've placed the hider somewhere on this map.",
  'In a real game, you will move around the map physically. In practice mode, tap on the map to move your position.',
  'Use the cards to narrow down the map to find where the hider is hiding. Good luck!',
] as const;

/** Jake's block, reassembled. Asserted against the lines by the copy tests. */
export const PRACTICE_INTRO_BODY = PRACTICE_INTRO_LINES.join(' ');

export const PRACTICE_INTRO_TITLE = 'Practice mode';

/**
 * Dismiss only. It must not read as a start button: nothing about the round
 * waits on this modal (acceptance 5), so a label promising to start something
 * would be a lie about what the tap does.
 */
export const PRACTICE_INTRO_DISMISS = 'Got it';

/**
 * The v19 hint, kept. The modal teaches; this reminds, for the rest of the
 * round, and it is now shared rather than an iOS-only string.
 */
export const PRACTICE_HINT = 'Tap the map to walk. Play cards to close in.';
